import { z } from 'zod'

/**
 * Enumerations mirroring docs/DOMAIN.md exactly. The Postgres enum for each
 * is named `<schema>_<name>` (see `pgEnumNames` below and packages/db).
 * Order matters: it is the Postgres enum order.
 */
function e<const T extends readonly [string, ...string[]]>(values: T) {
  return z.enum(values)
}

// identity
export const UserStatus = e(['active', 'suspended'])
export const ApiKeyOwnerKind = e(['user', 'agent'])

// agents
export const BrainProvider = e(['anthropic', 'openai', 'xai', 'google', 'other'])
export const BrainTier = e(['cheap', 'frontier'])
export const AgentKind = e(['platform', 'external'])
/**
 * `setting_up` (creation v2, docs/CREATION-V2.md): the agent exists and its token launches, but it has no strategy
 * version yet; it is public, never ticks and trades nothing until the interview's version 1 is approved (→ `active`).
 */
export const AgentStatus = e(['draft', 'setting_up', 'active', 'paused', 'retired'])
export const AgentVisibility = e(['public', 'unlisted'])
/** `agents.webhook_deliveries.status` (migration 0012). */
export const WebhookDeliveryStatus = e(['pending', 'delivered', 'failed'])
/**
 * Gate before live (docs/INTENTS.md §3): `agents.agent_versions.trial_state`. compile/apply → draft; a
 * backtest pass or inconclusive → backtested; running on a paper mandate → paper_trial; `trial.evaluate`
 * applies the forward-trial thresholds → eligible; bound to a live mandate → live. Only eligible (or live)
 * versions may trade live mandates.
 */
export const TrialState = e(['draft', 'backtested', 'paper_trial', 'eligible', 'live'])
export const RiskProfile = e(['conservative', 'balanced', 'degen'])

// social
export const PostKind = e(['observation', 'decision', 'trade', 'performance', 'note', 'launch', 'revision'])
export const PostVisibility = e(['public', 'followers', 'private'])
/** `social.notifications.kind` and `social.notification_preferences.kind` (migration 0012, API.md "Notifications"). */
export const NotificationKind = e([
  'trade_executed',
  'trade_refused',
  'proposal_failed',
  'permission_expiring',
  'permission_revoked',
  'plan_lapsed',
  'subscription_lapsed',
  'invoice_due',
  'reward_claimable',
  'executor_low_gas',
  'agent_revision_proposed',
  'token_launched',
  'system',
  // Lane BOOUX1 (migration 0067): the day's story of an agent the user follows (A2-11), and the end of a challenge the
  // user made or has an agent in (A2-15).
  'daily_story',
  'challenge_finished',
  // Lane CHATCORE1 (migration 0068, docs/ROOMS.md "Notifications"): someone mentioned the user in a room, replied to
  // the user's message, invited the user to a room, pinned the user's message, or wrote to the user directly; and
  // the hourly digest of a muted room's mentions and replies.
  'room_mention',
  'room_reply',
  'room_invite',
  'room_pin',
  'room_dm',
  'room_digest',
])

// evidence
export const EvidenceObjectType = e([
  'observation',
  'decision',
  'proposal',
  'policy_verdict',
  'execution',
  'fill',
  'performance_snapshot',
  'strategy_revision',
  'post',
  'delegation',
  'memory_episode',
  'memory_summary',
  'backtest',
  'intent_compilation',
  // The develop loop (migration 0040, docs/RUNTIME.md "Develop loop"): one object per step.
  'hypothesis',
  'experiment',
  'evaluation',
  'promotion',
])
export const ActorKind = e(['agent', 'user', 'runtime', 'system'])

// trading
/** A trading wallet arbitrage derives for the customer (`accounts.wallets.status`, arbitrage `Wallet.status`). */
export const TradingWalletStatus = e(['requested', 'ready', 'refused', 'frozen'])
/** A customer-signed permission (`accounts.permissions.status`, arbitrage `PermissionStatus`). */
export const PermissionStatus = e(['pending', 'active', 'paused', 'revoked', 'expired'])
/** What a permission lets the agent do on the wallet (arbitrage `Capability`, EIP-712 bit order). */
export const PermissionCapability = e(['spot', 'protective', 'copy', 'schedule', 'snipe', 'lp', 'launch'])
export const ProposalKind = e(['buy', 'sell', 'exit_all'])
export const ProposalStatus = e(['proposed', 'approved', 'refused', 'expired', 'cancelled', 'executing', 'executed', 'failed'])
export const QuoteSource = e(['arbitrage', 'onchain', 'mock'])
export const Verdict = e(['approved', 'refused'])
/** arbitrage order mode: a dry run (paper trial, or unarmed) or live. The platform holds the orders (algo.boo's `trading.orders` went with migration 0061). */
export const OrderMode = e(['live', 'dry_run'])
/** State of an arbitrage order: arbitrage's `OrderState`, plus `submitting` before arbitrage acknowledged it (algo.boo's mirror `trading.orders` went with migration 0061). */
export const ExecutionState = e(['submitting', 'accepted', 'triggered', 'signed', 'submitted', 'included', 'final', 'reorged', 'reverted', 'dropped', 'expired', 'refused', 'cancelled'])
export const PositionStatus = e(['open', 'closed'])
/**
 * Where an account, mandate or agent trades (migrations 0031, 0057; `@algoboo/schema/catalog` `VENUES`). `robinhood`:
 * Robinhood Chain (4663), live. `hyperliquid`: HyperCore through HyperEVM (999), coming soon. `solana`: Solana
 * mainnet-beta (no EVM chain id), coming soon. The API refuses a venue that is not live with 409
 * `VENUE_NOT_AVAILABLE` until the catalog marks it live.
 */
export const Venue = e(['robinhood', 'hyperliquid', 'solana'])

// billing
export const PlanId = e(['free', 'starter', 'pro'])
export const PlanSubscriptionStatus = e(['active', 'past_due', 'cancelled'])
export const AgentSubscriptionStatus = e(['active', 'cancelled', 'expired'])
export const EnvelopeOwnerKind = e(['user', 'agent', 'platform', 'room_agent'])
export const EnvelopeKind = e(['included', 'prepaid', 'daily_cap', 'protective'])
export const EnvelopeStatus = e(['open', 'exhausted', 'closed'])
export const ReservationStatus = e(['reserved', 'settled', 'released', 'expired'])
export const LedgerReason = e(['reserve', 'settle', 'release', 'expire', 'grant', 'topup', 'adjust'])
export const UsageKind = e(['llm', 'data', 'runtime'])
export const FeeCategory = e(['subscription', 'execution', 'performance'])
export const FeeSource = e(['onchain', 'offchain'])
/** `invoice`: a performance-fee invoice paid through PaymentTreasury (kind INVOICE, migration 0015). */
export const PaymentKind = e(['plan', 'topup', 'subscription', 'invoice', 'agent_creation'])
export const PaymentIntentStatus = e(['pending', 'confirmed', 'expired'])
/** `billing.invoices` (migration 0012): performance fees and renewals owed by a user. */
export const InvoiceKind = e(['performance_fee', 'plan_renewal', 'subscription_renewal'])
export const InvoiceStatus = e(['open', 'paid', 'overdue', 'void'])

// tokens
export const TokenStatus = e(['pending', 'live', 'halted'])
export const EpochStatus = e(['open', 'closed', 'claimable'])

// jobs
export const JobStatus = e(['queued', 'running', 'succeeded', 'failed', 'dead', 'cancelled'])
export const InboxKind = e(['permission_changed', 'budget_exhausted', 'user_message', 'delegation_result', 'system'])
export const InboxStatus = e(['pending', 'processed', 'dropped'])

/** Capability classes of ADR-0004 (grants to machines and keys). */
export const Capability = e(['read', 'write', 'spend', 'live'])

/** Auth classes of docs/API.md. */
export const AuthClass = e(['public', 'session', 'agent_key', 'session_or_agent_key', 'admin'])

/** Envelope kinds of `social.posts.kind` shown in the feed filter (`all` = no filter). */
export const FeedKindFilter = e(['all', 'observation', 'decision', 'trade', 'performance', 'launch'])
export const PerfRange = e(['24h', '7d', '30d', 'all'])
/** Which book a performance number comes from: paper accounts or live (on-chain) accounts, never mixed. */
export const PerformanceScope = e(['paper', 'live'])
/** `GET /agents/:handle/performance?scope=`: `all` = the agent's headline series (live when it has live accounts, else paper). */
export const PerformanceScopeFilter = e(['all', 'paper', 'live'])
export const AgentSort = e(['pnl', 'followers', 'aum', 'new'])
export const TokenSort = e(['volume', 'holders', 'distributed', 'new'])
export const QuoteExactness = e(['exact', 'bound', 'simulated'])
export const JobKind = e([
  // The platform's App API execution stream followed (docs/RUNTIME.md "arbitrage.events"): fills, fees, permission
  // and wallet changes, from a persisted cursor. An agent's strategy runs on the platform (lane BOO2).
  'arbitrage.events',
  'agent-stats.refresh',
  'epoch.close',
  'fees.reconcile',
  'budget.expire',
  'subscriptions.bill',
  'payments.index',
  'plans.expire',
  'notifications.deliver',
  'webhooks.deliver',
  'invoices.issue',
  'subscriptions.lapse',
  'creator-stats.refresh',
  // Daily partition maintenance (docs/RUNTIME.md "db.maintain"): monthly partitions ahead, price-sample retention.
  'db.maintain',
  // Social layer (docs/RUNTIME.md "social.activity", "social.milestones"): the live activity tape and milestone posts.
  'social.activity',
  'social.milestones',
  // Creation v2 (docs/CREATION-V2.md): the basics card of a launched agent from its description (model slot
  // `creation.basics`), posted into the agent's thread with the first interview question.
  'creation.basics',
  // Research is the platform's (lane BOO1, the owner's hard cut of 2026-09-27): a research program a person approved
  // in a thread is followed there -- its steps, evidence and candidates posted as they arrive, a final result offered
  // for adoption (docs/RUNTIME.md "Research programs").
  'research.program.watch',
  // Conversations (docs/CONVERSATIONS.md, Wave C): an agent turn after a user message (lane C2), the execution of an
  // approved proposal through the existing runtime paths (C2), and alerts and reports as thread messages (C4).
  'conversation.turn',
  'conversation.approval.execute',
  'conversation.alert',
  // The authoring loop (lane C3, docs/AUTHORING.md): intent → program → door check → repair → preview → proposal.
  'authoring.run',
  // The money loop on the platform (lane ALGOBOO1): every creation fee charged for a token launch the platform could not
  // carry out is filed as a refund with the platform and followed until its operator decides and it is paid back.
  'refunds.file',
  // An agent's registration and token launch are the platform's (lane ALGOBOO1): asked for once its fee is paid, and
  // followed until its token is live, or refused for good (its fee then refunded by `refunds.file`).
  'agent.launch',
  // A Boo is a platform agent (lane BOO2, the platform's v2.3 cut): its platform agent made and followed, its owner's
  // pause, resume and retire carried to it; and its tasks (a question asked in its thread, its scheduled reports) and
  // actions (an order, a payout) followed until each run and receipt ends, posted into the threads.
  'agent.principal',
  'agent.tasks.follow',
  // The platform's performance of each agent's strategy into `agent_stats` for the leaderboard (lane ALGOBOO1, PERF1).
  'performance.sync',
  // The fee loop (lane ALGOBOO1): each live token's holders from the platform; the monthly performance fee per
  // permission, assessed by the platform from its lots and invoiced here.
  'holders.sync',
  'performance.fees',
  // The daily story (lane ALGOBOO2, STORY1; lane BOOSTORY1): yesterday's story of every live agent, composed by
  // algo.boo from the platform's records of the day through the platform's model gateway.
  'story.request',
  // Lane BOOUX1 (A2-11, A2-15): each written day's story to the followers of its agent, once, and a finished
  // challenge's card on the tape with a note to the people in it.
  'story.notify',
  'challenges.close',
  // Rooms (lane CHATCORE1, docs/RUNTIME.md "Rooms"): a room's private platform source registered once; every room
  // message forwarded to it in order with its receipt kept; the notifications a message causes; the hourly digest of
  // muted rooms.
  'rooms.source',
  'rooms.forward',
  'rooms.notify',
  'rooms.digest',
  'rooms.agent.turn',
  'rooms.agent.watch',
])
/** `GET /feed?scope=`: everything, or only agents the caller follows (needs a session). */
export const FeedScope = e(['all', 'following'])


export type UserStatus = z.output<typeof UserStatus>
export type ApiKeyOwnerKind = z.output<typeof ApiKeyOwnerKind>
export type BrainProvider = z.output<typeof BrainProvider>
export type BrainTier = z.output<typeof BrainTier>
export type AgentKind = z.output<typeof AgentKind>
export type AgentStatus = z.output<typeof AgentStatus>
export type AgentVisibility = z.output<typeof AgentVisibility>
export type RiskProfile = z.output<typeof RiskProfile>
export type PostKind = z.output<typeof PostKind>
export type PostVisibility = z.output<typeof PostVisibility>
export type EvidenceObjectType = z.output<typeof EvidenceObjectType>
export type ActorKind = z.output<typeof ActorKind>
export type TradingWalletStatus = z.output<typeof TradingWalletStatus>
export type PermissionStatus = z.output<typeof PermissionStatus>
export type PermissionCapability = z.output<typeof PermissionCapability>
export type ProposalKind = z.output<typeof ProposalKind>
export type ProposalStatus = z.output<typeof ProposalStatus>
export type QuoteSource = z.output<typeof QuoteSource>
export type OrderMode = z.output<typeof OrderMode>
export type Verdict = z.output<typeof Verdict>
export type ExecutionState = z.output<typeof ExecutionState>
export type PositionStatus = z.output<typeof PositionStatus>
export type Venue = z.output<typeof Venue>
export type PlanId = z.output<typeof PlanId>
export type PlanSubscriptionStatus = z.output<typeof PlanSubscriptionStatus>
export type AgentSubscriptionStatus = z.output<typeof AgentSubscriptionStatus>
export type EnvelopeOwnerKind = z.output<typeof EnvelopeOwnerKind>
export type EnvelopeKind = z.output<typeof EnvelopeKind>
export type EnvelopeStatus = z.output<typeof EnvelopeStatus>
export type ReservationStatus = z.output<typeof ReservationStatus>
export type LedgerReason = z.output<typeof LedgerReason>
export type UsageKind = z.output<typeof UsageKind>
export type FeeCategory = z.output<typeof FeeCategory>
export type FeeSource = z.output<typeof FeeSource>
export type TokenStatus = z.output<typeof TokenStatus>
export type EpochStatus = z.output<typeof EpochStatus>
export type JobStatus = z.output<typeof JobStatus>
export type InboxKind = z.output<typeof InboxKind>
export type InboxStatus = z.output<typeof InboxStatus>
export type Capability = z.output<typeof Capability>
export type AuthClass = z.output<typeof AuthClass>
export type FeedKindFilter = z.output<typeof FeedKindFilter>
export type PerfRange = z.output<typeof PerfRange>
export type PerformanceScope = z.output<typeof PerformanceScope>
export type PerformanceScopeFilter = z.output<typeof PerformanceScopeFilter>
export type AgentSort = z.output<typeof AgentSort>
export type TokenSort = z.output<typeof TokenSort>
export type QuoteExactness = z.output<typeof QuoteExactness>
export type JobKind = z.output<typeof JobKind>
export type PaymentKind = z.output<typeof PaymentKind>
export type PaymentIntentStatus = z.output<typeof PaymentIntentStatus>
export type NotificationKind = z.output<typeof NotificationKind>
export type InvoiceKind = z.output<typeof InvoiceKind>
export type InvoiceStatus = z.output<typeof InvoiceStatus>
export type WebhookDeliveryStatus = z.output<typeof WebhookDeliveryStatus>
export type TrialState = z.output<typeof TrialState>
export type FeedScope = z.output<typeof FeedScope>

/**
 * Postgres enum name → values. packages/db builds its pgEnums from the same
 * literal lists and a test asserts they match.
 */
export const pgEnumValues = {
  identity_user_status: UserStatus.options,
  identity_api_key_owner_kind: ApiKeyOwnerKind.options,
  agents_brain_provider: BrainProvider.options,
  agents_brain_tier: BrainTier.options,
  agents_agent_kind: AgentKind.options,
  agents_agent_status: AgentStatus.options,
  agents_agent_visibility: AgentVisibility.options,
  agents_webhook_delivery_status: WebhookDeliveryStatus.options,
  agents_trial_state: TrialState.options,
  social_post_kind: PostKind.options,
  social_post_visibility: PostVisibility.options,
  social_notification_kind: NotificationKind.options,
  evidence_object_type: EvidenceObjectType.options,
  evidence_actor_kind: ActorKind.options,
  trading_venue: Venue.options,
  billing_plan_subscription_status: PlanSubscriptionStatus.options,
  billing_agent_subscription_status: AgentSubscriptionStatus.options,
  billing_envelope_owner_kind: EnvelopeOwnerKind.options,
  billing_envelope_kind: EnvelopeKind.options,
  billing_envelope_status: EnvelopeStatus.options,
  billing_reservation_status: ReservationStatus.options,
  billing_ledger_reason: LedgerReason.options,
  billing_usage_kind: UsageKind.options,
  billing_fee_category: FeeCategory.options,
  billing_fee_source: FeeSource.options,
  billing_payment_kind: PaymentKind.options,
  billing_payment_intent_status: PaymentIntentStatus.options,
  billing_invoice_kind: InvoiceKind.options,
  billing_invoice_status: InvoiceStatus.options,
  tokens_token_status: TokenStatus.options,
  tokens_epoch_status: EpochStatus.options,
  jobs_job_status: JobStatus.options,
  jobs_inbox_kind: InboxKind.options,
  jobs_inbox_status: InboxStatus.options,
} as const satisfies Record<string, readonly string[]>
